import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Grid2 as Grid,
  Chip,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  AlertTitle,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  InputAdornment,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  Percent as PercentIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  History as HistoryIcon,
  Payments as CashIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import {
  topupRatesService,
  TOPUP_INSTRUMENTS,
  TopUpRates,
  TopUpRatesVersion,
  TopUpInstrument,
} from '../../../services';
import CashLimitsPanel from './CashLimitsPanel';

/** Etiquetas dos instrumentos, na ordem em que interessam ao negócio. */
const INSTRUMENT_LABELS: Record<TopUpInstrument, string> = {
  CASH: 'Numerário (balcão / POS)',
  VINTI4: 'Vinti4 (cartão nacional)',
  VISA: 'Visa',
  MASTERCARD: 'Mastercard',
  BANK_TRANSFER: 'Transferência bancária',
};

const bpsToPct = (bps: number | null | undefined): string =>
  bps == null ? '' : (bps / 100).toFixed(2);

const pctToBps = (pct: string): number | null => {
  const n = Number(String(pct).replace(',', '.'));
  if (!Number.isFinite(n)) return null;
  return Math.round(n * 100);
};

const cve = (n: number | null | undefined): string =>
  n == null ? '—' : `${n.toLocaleString('pt-PT')}$`;

export default function TopUpRatesPage() {
  const [rates, setRates] = useState<TopUpRates | null>(null);
  const [history, setHistory] = useState<TopUpRatesVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Formulário: percentagens como texto, para não lutar com o cursor do input
  const [form, setForm] = useState<Record<string, string>>({});
  const [limits, setLimits] = useState({ min: '', max: '' });
  const [motivo, setMotivo] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [current, hist] = await Promise.all([
        topupRatesService.get(),
        topupRatesService.history().catch(() => [] as TopUpRatesVersion[]),
      ]);
      setRates(current);
      setHistory(hist);
      setForm(
        TOPUP_INSTRUMENTS.reduce((acc, i) => {
          acc[i] = bpsToPct(current.ratesBps[i]);
          return acc;
        }, {} as Record<string, string>),
      );
      setLimits({ min: String(current.minTopUpCVE), max: String(current.maxTopUpCVE) });
      setMotivo('');
    } catch (err: any) {
      // O texto de recurso dizia «confirme que existe um contrato activo» — e
      // mandava procurar o contrário do problema: o servidor recusa tanto com
      // ZERO activos como com MAIS DO QUE UM. Com dois promotores activos não há
      // resposta certa, e escolher um cobraria a taxa de um ao outro.
      setError(
        err?.response?.data?.error ?? err?.message ??
        'Não foi possível carregar as taxas.',
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  /** O que mudou em relação ao que está em vigor. */
  const changes = useMemo(() => {
    if (!rates) return [];
    const out: { label: string; de: string; para: string }[] = [];

    for (const i of TOPUP_INSTRUMENTS) {
      const antes = rates.ratesBps[i];
      const agora = pctToBps(form[i] ?? '');
      if (form[i] === '' && antes == null) continue;
      if (agora !== antes) {
        out.push({
          label: INSTRUMENT_LABELS[i],
          de: antes == null ? 'não definida' : `${bpsToPct(antes)}%`,
          para: agora == null ? 'inválida' : `${bpsToPct(agora)}%`,
        });
      }
    }
    if (Number(limits.min) !== rates.minTopUpCVE) {
      out.push({ label: 'Carregamento mínimo', de: cve(rates.minTopUpCVE), para: cve(Number(limits.min)) });
    }
    if (Number(limits.max) !== rates.maxTopUpCVE) {
      out.push({ label: 'Carregamento máximo', de: cve(rates.maxTopUpCVE), para: cve(Number(limits.max)) });
    }
    return out;
  }, [rates, form, limits]);

  /** Pré-visualização do balcão com a taxa que está no formulário. */
  const cashPreview = useMemo(() => {
    const bps = pctToBps(form.CASH ?? '');
    if (bps == null) return null;
    const entregue = 1000;
    // Espelha o motor: Math.floor sobre inteiros, taxa deduzida do entregue
    const taxa = Math.floor((entregue * bps) / 10000);
    return { entregue, taxa, creditado: entregue - taxa };
  }, [form.CASH]);

  const formInvalid = useMemo(() => {
    for (const i of TOPUP_INSTRUMENTS) {
      const v = form[i];
      if (v === '' || v == null) continue;
      const bps = pctToBps(v);
      if (bps == null || bps < 0 || bps > 5000) return `Taxa de ${INSTRUMENT_LABELS[i]} inválida (0% a 50%).`;
    }
    const min = Number(limits.min);
    const max = Number(limits.max);
    if (!Number.isInteger(min) || min < 0) return 'Mínimo inválido.';
    if (!Number.isInteger(max) || max < 0) return 'Máximo inválido.';
    if (min > max) return 'O mínimo não pode ser maior que o máximo.';
    return null;
  }, [form, limits]);

  const publish = async () => {
    if (!rates) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const ratesBps = TOPUP_INSTRUMENTS.reduce((acc, i) => {
        const bps = pctToBps(form[i] ?? '');
        if (bps != null) acc[i] = bps;
        return acc;
      }, {} as Partial<Record<TopUpInstrument, number>>);

      const result = await topupRatesService.publish({
        ratesBps,
        minTopUpCVE: Number(limits.min),
        maxTopUpCVE: Number(limits.max),
        motivo: motivo.trim() || undefined,
      });

      setSuccess(
        `Versão ${result.version} publicada e em vigor. A versão ${result.anterior.version} foi arquivada.`,
      );
      setConfirmOpen(false);
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Não foi possível publicar as taxas.');
      setConfirmOpen(false);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600 }}>
            <PercentIcon color="primary" /> Taxas de carregamento
          </Typography>
          <Typography variant="body2" color="text.secondary">
            O que a Pagali cobra sobre cada carregamento de carteira, por meio de pagamento.
          </Typography>
        </Box>
        <Button startIcon={<RefreshIcon />} onClick={load} disabled={saving}>
          Actualizar
        </Button>
      </Box>

      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess(null)}>{success}</Alert>}

      {rates && (
        <Alert severity="info" icon={<InfoIcon />}>
          <AlertTitle>Publicar cria uma versão nova</AlertTitle>
          Os contratos não se alteram no lugar — publicar taxas novas cria a versão seguinte e arquiva a
          actual. Os carregamentos já feitos continuam a explicar-se pela versão com que foram calculados.
          Em vigor: <strong>versão {rates.version}</strong> ({rates.contractId}).
        </Alert>
      )}

      <Card>
        <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Taxa por meio de pagamento
          </Typography>

          <Grid container spacing={2}>
            {TOPUP_INSTRUMENTS.map((i) => (
              <Grid key={i} size={{ xs: 12, sm: 6, md: 4 }}>
                <TextField
                  fullWidth
                  label={INSTRUMENT_LABELS[i]}
                  value={form[i] ?? ''}
                  onChange={(e) => setForm((f) => ({ ...f, [i]: e.target.value }))}
                  placeholder={bpsToPct(rates?.defaultRateBps)}
                  helperText={
                    form[i] === '' || form[i] == null
                      ? `Sem taxa própria — usa a de omissão (${bpsToPct(rates?.defaultRateBps)}%)`
                      : `${pctToBps(form[i]) ?? '—'} pontos base`
                  }
                  slotProps={{
                    input: { endAdornment: <InputAdornment position="end">%</InputAdornment> },
                  }}
                />
              </Grid>
            ))}
          </Grid>

          <Divider />

          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Limites por carregamento
          </Typography>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                fullWidth
                label="Mínimo"
                value={limits.min}
                onChange={(e) => setLimits((l) => ({ ...l, min: e.target.value }))}
                slotProps={{ input: { endAdornment: <InputAdornment position="end">CVE</InputAdornment> } }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                fullWidth
                label="Máximo"
                value={limits.max}
                onChange={(e) => setLimits((l) => ({ ...l, max: e.target.value }))}
                helperText="Aplica-se ao valor entregue em mão, no caso do numerário"
                slotProps={{ input: { endAdornment: <InputAdornment position="end">CVE</InputAdornment> } }}
              />
            </Grid>
          </Grid>

          {cashPreview && (
            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'action.hover' }}>
              <Typography variant="overline" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <CashIcon fontSize="small" /> Ao balcão, com esta taxa
              </Typography>
              <Typography variant="body1" sx={{ mt: 0.5 }}>
                Cliente entrega <strong>{cve(cashPreview.entregue)}</strong> &rarr; taxa de{' '}
                <strong>{cve(cashPreview.taxa)}</strong> &rarr; carteira recebe{' '}
                <strong>{cve(cashPreview.creditado)}</strong>.
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Em numerário a taxa é deduzida do valor entregue, para não haver troco a dar.
              </Typography>
            </Paper>
          )}

          {formInvalid && <Alert severity="warning">{formInvalid}</Alert>}

          {changes.length > 0 && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="overline" color="text.secondary">
                Alterações a publicar
              </Typography>
              <Box component="ul" sx={{ m: 0, mt: 1, pl: 2.5 }}>
                {changes.map((c) => (
                  <li key={c.label}>
                    <Typography variant="body2">
                      {c.label}: <s>{c.de}</s> &rarr; <strong>{c.para}</strong>
                    </Typography>
                  </li>
                ))}
              </Box>
            </Paper>
          )}

          <TextField
            fullWidth
            label="Motivo da alteração"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="Ex.: revisão anual de preçário"
            helperText="Fica no registo da versão, para quem olhar para isto daqui a um ano"
          />

          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <Tooltip title={changes.length === 0 ? 'Nada mudou em relação ao que está em vigor' : ''}>
              <span>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  disabled={saving || changes.length === 0 || !!formInvalid}
                  onClick={() => setConfirmOpen(true)}
                >
                  Publicar nova versão
                </Button>
              </span>
            </Tooltip>
            <Button disabled={saving || changes.length === 0} onClick={load}>
              Descartar alterações
            </Button>
          </Box>
        </CardContent>
      </Card>

      <CashLimitsPanel />

      <Card>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <HistoryIcon fontSize="small" /> Versões
          </Typography>

          {history.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Sem histórico para mostrar.
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Versão</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell>Numerário</TableCell>
                    <TableCell>Vinti4</TableCell>
                    <TableCell align="right">Mín.</TableCell>
                    <TableCell align="right">Máx.</TableCell>
                    <TableCell>Publicada por</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {history.map((v) => (
                    <TableRow key={v.contractId} hover>
                      <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>v{v.version}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={v.status}
                          color={v.status === 'ACTIVE' ? 'success' : 'default'}
                          variant={v.status === 'ACTIVE' ? 'filled' : 'outlined'}
                        />
                      </TableCell>
                      <TableCell>{v.ratesBps.CASH != null ? `${bpsToPct(v.ratesBps.CASH)}%` : '—'}</TableCell>
                      <TableCell>{v.ratesBps.VINTI4 != null ? `${bpsToPct(v.ratesBps.VINTI4)}%` : '—'}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{cve(v.minTopUpCVE)}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{cve(v.maxTopUpCVE)}</TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">{v.createdBy}</Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Publicar taxas novas?</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            Vai passar a cobrar isto em todos os carregamentos feitos a partir de agora.
            <Box component="ul" sx={{ mt: 1.5, pl: 2.5 }}>
              {changes.map((c) => (
                <li key={c.label}>
                  {c.label}: <s>{c.de}</s> &rarr; <strong>{c.para}</strong>
                </li>
              ))}
            </Box>
            {cashPreview && (
              <Box sx={{ mt: 1 }}>
                Ao balcão: entrega de {cve(cashPreview.entregue)} passa a creditar{' '}
                <strong>{cve(cashPreview.creditado)}</strong>.
              </Box>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={saving}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={publish}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
          >
            Publicar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
